<template>
  <div class="wrapper">
    <div v-if="imageUrl" class="image-wrapper" :title="imageName" @click="clickToUpload">
      <img :src="imageUrl" class="preview-img">
      <div class="image-info-overlay">
        <span class="info-text">{{ imageName }}</span>
      </div>
      <div v-if="!disabled" class="delete-image-button" @click.stop="handleDeleteFile">
        <span>Delete</span>
      </div>
    </div>
    <div v-else class="file-input-wrapper" @click="$refs.fileInput.click()">
      <label class="placeholder-text">{{ placeholder }}</label>
      <input
        ref="fileInput"
        type="file"
        :accept="acceptTypes"
        :disabled="disabled"
        class="file-input"
        @change="handleFileInputChange"
      >
    </div>
    <input
      ref="hiddenFileInput"
      type="file"
      :accept="acceptTypes"
      :disabled="disabled"
      style="display: none"
      @change="handleFileInputChange"
    >
  </div>
</template>

<script>
import ImageCropModalComponent from '@/shared/image-crop-modal/ImageCropModalComponent.vue';

export default {
  name: 'ImageWithPreviewField',

  props: {
    imagePath: {
      type: String,
      required: false,
      default: '',
    },
    imageName: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Select image file',
    },
    accept: {
      type: Array,
      required: false,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // When both cropWidth and cropHeight are provided, the picked image will
    // be opened in a crop modal and forced to that exact output resolution
    // before it is emitted via the onChange event.
    cropWidth: {
      type: Number,
      required: false,
      default: null,
    },
    cropHeight: {
      type: Number,
      required: false,
      default: null,
    }
  },

  data() {
    return {
      image: null,
      imageUrl: '',
    }
  },

  computed: {
    acceptTypes() {
      return this.accept.join(',')
    },

    shouldCrop() {
      return !!this.cropWidth && !!this.cropHeight;
    },
  },

  watch: {
    imageName() {
      this.image = this.imageName;
    },

    imagePath() {
      this.imageUrl = this.imagePath
    }
  },

  created() {
    this.image = this.imageName;
    this.imageUrl = this.imagePath;
  },

  methods: {
    handleFileInputChange(event) {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.handleFilePick(files);
      }
    },

    handleFilePick(files) {
      const file = files[0];

      if (!file) {
        return;
      }

      if (this.shouldCrop) {
        this.openCropModal(file);
        return;
      }

      this.imageUrl = URL.createObjectURL(file);
      this.$emit('onChange', file);
    },

    openCropModal(file) {
      const objectUrl = URL.createObjectURL(file);

      this.$modal.show(
        ImageCropModalComponent,
        {
          imageSrc: objectUrl,
          fileName: file.name,
          fileType: file.type || 'image/png',
          cropWidth: this.cropWidth,
          cropHeight: this.cropHeight,
          onCrop: (croppedFile) => {
            URL.revokeObjectURL(objectUrl);
            this.imageUrl = URL.createObjectURL(croppedFile);
            this.$emit('onChange', croppedFile);
            this.resetFileInputs();
          },
          onClose: () => {
            URL.revokeObjectURL(objectUrl);
            this.resetFileInputs();
          },
        },
        { height: 'auto', scrollable: true, width: '800px' }
      );
    },

    resetFileInputs() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      if (this.$refs.hiddenFileInput) {
        this.$refs.hiddenFileInput.value = '';
      }
    },

    handleDeleteFile() {
      this.imageUrl = '';
      this.image = null;
      this.$emit('onChange', null);
    },

    clickToUpload() {
      if (!this.disabled) {
        this.$refs.hiddenFileInput.click();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 200px;
  max-width: 400px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.preview-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-info-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.75rem 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-wrapper:hover .image-info-overlay {
  opacity: 1;
}

.info-text {
  color: white;
  font-size: 0.9rem;
  word-break: break-all;
  display: block;
}

.delete-image-button {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: var(--brand-600);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  color: #fff;
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 13px;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--brand-700);
  }
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 420px;
  padding: 24px;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-subtle);
  cursor: pointer;
  transition: border-color var(--duration-fast) ease, background-color var(--duration-fast) ease;
  position: relative;

  &:hover {
    border-color: var(--brand-600);
    background: var(--brand-50);
  }
}

.placeholder-text {
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  pointer-events: none;
}

.file-input {
  display: none;
}
</style>
